import { theme } from "antd";
import { FC, Fragment, PropsWithChildren } from "react";
import { asKey } from "src/utils/fields";
import { attributeToTitle } from "src/utils/strings";
import { formatTime } from "src/utils/date";
import { Comparator, SearchState } from "src/search/types";
import { mono } from "src/fonts";

export const TextualQuery = ({
  hovering,
  state,
  ...components
}: {
  hovering?: boolean;
  state: SearchState;
  Sep?: FC;
  Val?: FC<PropsWithChildren<{ hovering?: boolean }>>;
}) => {
  const empty =
    !state.search &&
    !state.duration &&
    !state.startDate &&
    !state.endDate &&
    !state.aggregations?.length &&
    !state.groupBys?.length &&
    !state.filters?.length &&
    !state.select?.length;

  const Sep = components.Sep || DefaultSep;
  const Val = components.Val || DefaultVal;
  return (
    <Fragment>
      {empty && (
        <>
          <span className={mono.className}>empty query</span>
          <Sep />
        </>
      )}
      {state.search && (
        <span>
          <span className={mono.className}>Search </span>
          <Val hovering={hovering}>{state.search}</Val>
          <Sep />
        </span>
      )}
      {state.select && state.select.length > 0 && (
        <span>
          <span className={mono.className}>Looking for </span>
          <Val hovering={hovering} key={state.select[0]}>
            {attributeToTitle(state.select[0])}
          </Val>
          <Sep />
        </span>
      )}
      {state.groupBys && state.groupBys.length > 0 && (
        <span>
          <span className={mono.className}>By </span>
          {state.groupBys.map((g, i) => (
            <Fragment key={asKey(g)}>
              {i > 0 && <span>, </span>}
              <Val hovering={hovering}>{attributeToTitle(asKey(g))}</Val>
            </Fragment>
          ))}
          <Sep />
        </span>
      )}
      {state.aggregations && state.aggregations.length > 0 && (
        <span>
          <span className={mono.className}>Measure </span>
          {state.aggregations.map((g, i) => (
            <Fragment key={`${asKey(g)}-${i}`}>
              {i > 0 && <span>, </span>}
              <Val hovering={hovering}>{attributeToTitle(asKey(g))}</Val>
            </Fragment>
          ))}

          <Sep />
        </span>
      )}
      {state.filters && state.filters.length > 0 && (
        <span>
          <span>
            <span className={mono.className}>Where </span>
            {state.filters.map((f, i) => (
              <span
                key={`${f.key || asKey(f)}-${Object.values(
                  f.comparison
                )[0].join(",")}`}
              >
                {i > 0 && <>, </>}
                <Val hovering={hovering}>
                  {attributeToTitle(asKey(f))}{" "}
                  {Object.keys(f.comparison)[0] === "WITHIN" ? (
                    <NumbersConditionText comparison={f.comparison} />
                  ) : (
                    <StringsConditionText comparison={f.comparison} />
                  )}
                </Val>
              </span>
            ))}
          </span>
          <Sep />
        </span>
      )}
      {state.duration && (
        <span>
          <span className={mono.className}>When </span>
          <Val hovering={hovering}>{state.duration}</Val>
          <Sep />
        </span>
      )}
      {state.startDate && state.endDate && (
        <span>
          <span className={mono.className}>When </span>
          <Val hovering={hovering}>
            {formatTime(state.startDate, "YYYY-MM-DD")}
          </Val>{" "}
          <span className={mono.className}>- </span>
          <Val hovering={hovering}>
            {formatTime(state.endDate, "YYYY-MM-DD")}
          </Val>
          <Sep />
        </span>
      )}
      {state.compareWith && (
        <span>
          <span className={mono.className}>Compare to </span>
          <Val hovering={hovering}>{state.compareWith.ago}</Val>
          <Sep />
        </span>
      )}
    </Fragment>
  );
};

function StringsConditionText({ comparison }: { comparison: Comparator }) {
  return (
    <>
      {Object.keys(comparison)[0] === "NOT IN" ? "!=" : "="}{" "}
      {Object.values(comparison)[0]
        .map((v) => (v === "__VZBL::null__" ? "n/a" : v))
        .join(", ")}
    </>
  );
}

function NumbersConditionText({ comparison }: { comparison: Comparator }) {
  const lte = Object.values(comparison)[0][0] === "__VZBL::null__";
  const gte = Object.values(comparison)[0][1] === "__VZBL::null__";
  const nums = Object.values(comparison)[0];
  if (gte)
    return (
      <>
        {">="}
        {nums[0]}
      </>
    );
  if (lte)
    return (
      <>
        {"<="}
        {nums[1]}
      </>
    );
  return (
    <>
      {">="}
      {nums[0]}
      {" && "}
      {"<="}
      {nums[1]}
    </>
  );
}

const DefaultSep = () => (
  <span
    style={{
      display: "inline-block",
      paddingLeft: 6,
      paddingRight: 6,
      opacity: 0.3,
    }}
  >
    |
  </span>
);

const DefaultVal = ({
  children,
  hovering,
}: PropsWithChildren<{ hovering?: boolean }>) => {
  const { token } = theme.useToken();
  return (
    <span
      className={mono.className}
      style={{
        color: hovering ? token.colorPrimary : token.colorTextSecondary,
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
};
